import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

type PageFetcher = (cursor?: string) => Promise<{ items: string[]; nextCursor?: string | null }>;

@customElement('infinite-image-grid')
export class InfiniteImageGrid extends LitElement {
  createRenderRoot() { return this; }

  @state()
  items: string[] = [];

  @property({ type: Boolean })
  shareCta = true;

  @property({ attribute: false })
  fetchPage?: PageFetcher;

  @property({ type: Boolean })
  autoLoad = false;

  gridClass = 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3';

  @state()
  private cursor: string | null = null;
  @state()
  private hasMore = false;
  @state()
  private loading = false;

  private io?: IntersectionObserver;
  private sentinel?: HTMLElement;

  connectedCallback(): void {
    super.connectedCallback();
    if (this.fetchPage && this.autoLoad && this.items.length === 0) {
      this.loadFirst();
    }
  }

  firstUpdated(): void {
    if (!this.fetchPage) {
      return;
    }

    this.io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          this.loadMore();
        }
      }
    }, { root: null, rootMargin: '600px 0px', threshold: 0 });
    this.sentinel = this.querySelector('#iig-sentinel') as HTMLElement;
    if (this.sentinel) this.io.observe(this.sentinel);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this.io && this.sentinel) this.io.unobserve(this.sentinel);
    this.io = undefined; this.sentinel = undefined;
  }

  public async reload() {
    this.items = [];
    this.cursor = null;
    this.hasMore = false;
    await this.loadFirst();
  }

  private async loadFirst() {
    if (!this.fetchPage) return;
    this.loading = true;
    try {
      const page = await this.fetchPage(undefined);
      this.items = page.items || [];
      this.cursor = page.nextCursor ?? null;
      this.hasMore = !!page.nextCursor;
    } catch { /* ignore */ }
    finally { this.loading = false; }
  }

  private async loadMore() {
    if (!this.fetchPage || this.loading || !this.hasMore) return;
    this.loading = true;
    try {
      const page = await this.fetchPage(this.cursor || undefined);
      const next = page.items || [];
      if (next.length) this.items = [...this.items, ...next];
      this.cursor = page.nextCursor ?? null;
      this.hasMore = !!page.nextCursor;
    } catch (e) {
      console.error(e)
    } finally {
      this.loading = false;
    }
  }

  private onOpen(index: number) {
    this.dispatchEvent(new CustomEvent('open', {
      detail: { index, url: this.items[index] },
      bubbles: true, composed: true
    }));
  }

  private skeleton() {
    return html`
      <div class="rounded-2xl border border-white/10 bg-slate-900/40 p-2">
        <div class="h-72 md:h-80 w-full rounded-xl bg-slate-800/80 animate-pulse"></div>
      </div>
    `;
  }

  render() {
    const isStatic = !this.fetchPage;

    return html`
      <div class=${this.gridClass}>
        ${this.items.map((url, i) => html`
          <figure
            class="relative overflow-hidden rounded-2xl border border-white/10 bg-slate-900/60 group cursor-pointer"
            @click=${() => this.onOpen(i)}
            role="button" tabindex="0"
          >
            <img src=${url} class="w-full h-72 md:h-80 object-contain bg-black/20" loading="lazy" />
            ${this.shareCta ? html`
              <div class="pointer-events-none absolute inset-x-0 bottom-0 p-3 flex justify-center">
                <div class="pointer-events-auto opacity-90 group-hover:opacity-100 transition">
                  <button class="rounded-full px-4 py-2 text-sm font-medium border border-white/20 bg-black/40 backdrop-blur hover:bg-black/60">
                    Post
                  </button>
                </div>
              </div>
            ` : null}
          </figure>
        `)}

        ${!this.items.length && this.loading ? Array.from({ length: 8 }).map(() => this.skeleton()) : null}
      </div>

      ${isStatic ? null : html`
        <div id="iig-sentinel" class="h-12 grid place-items-center text-slate-400">
          ${this.loading && this.items.length ? html`<span class="text-xs">Loading more…</span>`
          : !this.hasMore && this.items.length ? html`<span class="text-xs"></span>`
            : html`<span class="text-xs">&nbsp;</span>`}
        </div>
      `}
    `;
  }
}

